import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { FullPageLoader, RegularModal } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { Table } from '@/components_deprecated/Table'
import { parseISO } from 'date-fns'
import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useBreadcrumbs } from '@/utils/hooks'
import type {
  VehicleColorDictionary} from '../activities';
import {
  enforceHourMinuteString,
  generateActivityMapColors
} from '../activities'
import { MainInfo, Map, TripInfo } from '../activities/components/detail'
import type { SpecificConditionNode } from '../activities/types.graphql'
import type { WorkPeriodNode } from './types.graphql'
import { WORK_PERIOD } from './queries'
import { useQuery } from '@apollo/client'
import type { WorkPeriodQueryResult, WorkPeriodQueryVariables } from './types'

const StyledTable = styled(Table)`
  grid-area: sums;
  margin: 0.5rem 0;
`

const StyledMap = styled(Map)``

const Content = styled(Card)`
  display: grid;
  grid-template-columns: 2fr minmax(300px, 1fr);
  grid-template-rows: 120px 380px auto;
  grid-template-areas:
    'main-info map'
    'trip-info map'
    'sums sums'
    'schedule  schedule';
  padding: 0;

  ${props => props.theme.media.mobile} {
    grid-template-columns: 100%;
    grid-template-rows: 280px auto;
    grid-template-areas:
      'map'
      'main-info'
      'trip-info'
      'sums'
      'schedule';

    overflow: hidden;
  }
`

const StyledRegularModal = styled(RegularModal)`
  padding: 0;

  & > div {
    display: none;
  }

  & > div.content {
    display: block;
  }

  .content {
    padding: 0;
  }
`

const CancelWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem 0.5rem 1rem 1rem;

  & > i {
    cursor: pointer;
  }
`

export interface SpecificCondition extends SpecificConditionNode {
  datetimeStart: string
  datetimeEnd: string | null
}

interface WorkPeriodDetailProps {
  workPeriodId: string
  registrationNumbers?: string[]
  onSubmit: (workPeriod: WorkPeriodNode | null) => void
}

export const WorkPeriodDetail: React.JSX.CFC<WorkPeriodDetailProps> = ({
  workPeriodId,
  onSubmit,
}) => {
  const translations = useTranslate({
    loading: 'common.loading',
    drivingData: 'common.driving-data',
    diet: 'common.diet',
    salaryDay: 'common.salary-day',
    showSums: 'common.show-sums',

    activity: 'common.activity',
    value: 'common.value',
    timeDriving: 'common.driving',
    timeRest: 'common.rest',
    timePause: 'common.pause',
    timePaidRest: 'activities.paid-pause',
    timeAvailability: 'common.availability',
    timeOtherWork: 'common.other-work',
    timeOvertime: 'common.overtime',
    timeWorkTotal: 'common.total-work',

    paid: 'common.paid',
    unpaid: 'common.unpaid',
  })

  const { setOverride, setOverrides, overrides } = useBreadcrumbs()

  const { data, loading } = useQuery<
    WorkPeriodQueryResult,
    WorkPeriodQueryVariables
  >(WORK_PERIOD, {
    variables: { id: workPeriodId },
  })

  const workPeriod = data?.workPeriod

  const vehicleLocations = workPeriod?.drivenCoordinates ?? []
  const userId = workPeriod?.user.id ?? ''

  useEffect(() => {
    if (!workPeriod && overrides[userId] !== translations.loading)
      setOverride(userId, translations.loading)
    else if (workPeriod && overrides[userId] === translations.loading) {
      const { user } = workPeriod
      setOverrides({
        [user.id]: user.fullName,
      })
    }
  }, [
    setOverride,
    setOverrides,
    translations.loading,
    userId,
    overrides,
    workPeriod,
  ])

  // We want the color to be consistent for each vehicle. Vehicle Vin is used as a [key: color]
  const vehicleColors = useMemo<VehicleColorDictionary>(() => {
    const vehicleObject: VehicleColorDictionary = {}
    const vehicleVinList =
      workPeriod?.drivenCoordinates
        .map(drivenCoordinate => drivenCoordinate.vehicleVin ?? 'Unknown')
        .filter(
          (value, index, vehicles) => vehicles.indexOf(value) === index
        ) ?? []
    const vehicleColorList = generateActivityMapColors(vehicleVinList.length)

    vehicleVinList.forEach((vehicle, idx) => {
      vehicleObject[vehicle] = vehicleColorList[idx] ?? '#0088FF'
    })

    return vehicleObject
  }, [workPeriod?.drivenCoordinates])

  const user = workPeriod?.user ?? null

  const registrationNumbers =
    workPeriod?.activities?.edges
      .map(edge => edge?.node?.vehicleRegistrationNumber)
      .filter((value, index, vehicles) => vehicles.indexOf(value) === index)
      .filter(value => value !== undefined) ?? []

  if (loading || !workPeriod) {
    return <FullPageLoader />
  }

  const startLocation = workPeriod.startLocation
  const startLocationKm = startLocation
    ? startLocation.totalKmDriven
    : undefined
  const startLocationFuel = startLocation
    ? startLocation.totalFuelUsed
    : undefined

  const endLocation = workPeriod.endLocation
  const endLocationKm = endLocation ? endLocation.totalKmDriven : undefined
  const endLocationFuel = endLocation ? endLocation.totalFuelUsed : undefined

  const distance =
    startLocationKm && endLocationKm
      ? (endLocationKm - startLocationKm).toFixed(2)
      : undefined
  const totalFuelUsed =
    startLocationFuel && endLocationFuel
      ? (endLocationFuel - startLocationFuel).toFixed(2)
      : undefined

  return (
    <StyledRegularModal
      overflowY="auto"
      cancelOnClickOutside
      onCancel={() => onSubmit(null)}
      width="calc(100vw - 2rem)"
    >
      <CancelWrapper>
        <Icon icon="times" onClick={() => onSubmit(null)} />
      </CancelWrapper>

      <Content>
        <MainInfo
          user={user}
          date={
            workPeriod?.datetimeStart
              ? parseISO(workPeriod.datetimeStart)
              : new Date()
          }
          registrationNumbers={registrationNumbers}
          loading={false}
        />

        <TripInfo
          startTime={workPeriod?.datetimeStart}
          endTime={workPeriod?.datetimeEnd}
          startLocation={startLocation?.details?.description ?? undefined}
          endLocation={endLocation?.details?.description ?? undefined}
          timeWorkTotal={enforceHourMinuteString(
            workPeriod?.activityTimes?.timeWorkTotal
          )}
          distance={distance ?? undefined}
          fuelConsumption={totalFuelUsed ?? undefined}
          driverGrade={undefined}
          drivingGrade={undefined}
        />

        <StyledMap
          pathColors={vehicleColors}
          vehicleLocations={vehicleLocations}
        />

        {/* Map activities on a one line graph with different colors for each activityType. Similar to Schedule.tsx. Inline, not on a map. */}
        <StyledTable noBorders noShadow>
          <thead>
            <tr>
              <th>{translations.activity}</th>
              <th>{translations.value}</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>{translations.timeWorkTotal}</td>
              <td>
                {enforceHourMinuteString(
                  workPeriod?.activityTimes?.timeWorkTotal
                )}
              </td>
            </tr>

            <tr>
              <td>{translations.timeDriving}</td>
              <td>
                {enforceHourMinuteString(
                  workPeriod?.activityTimes?.timeDriving
                )}
              </td>
            </tr>

            <tr>
              <td>{translations.timeOtherWork}</td>
              <td>
                {enforceHourMinuteString(
                  workPeriod?.activityTimes?.timeOtherWork
                )}
              </td>
            </tr>

            <tr>
              <td>{translations.timeAvailability}</td>
              <td>
                {enforceHourMinuteString(
                  workPeriod?.activityTimes?.timeAvailability
                )}
              </td>
            </tr>

            <tr>
              <td>{translations.timePause}</td>
              <td>
                {translations.paid}:{' '}
                {enforceHourMinuteString(
                  workPeriod?.activityTimes?.timePaidRest
                )}{' '}
                <br />
                {translations.unpaid}:{' '}
                {enforceHourMinuteString(workPeriod?.activityTimes?.timePause)}
              </td>
            </tr>
          </tbody>
        </StyledTable>
      </Content>
    </StyledRegularModal>
  )
}
